import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import api from "../api";

function toMysqlDatetime(date) {
  const pad = (n) => (n < 10 ? "0" + n : n);
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    " " +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds())
  );
}

const RANGE_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "year", label: "Year" },
];

const COLORS = [
  "#6366f1", "#10b981", "#f59e0b", "#ec4899",
  "#3b82f6", "#8b5cf6", "#ef4444", "#14b8a6",
  "#f97316", "#84cc16",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb",
      borderRadius: "10px", padding: "10px 14px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: "12px",
    }}>
      <div style={{ fontWeight: 700, color: "#374151", marginBottom: "6px" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
          <span style={{ color: "#6b7280" }}>{p.dataKey}:</span>
          <span style={{ fontWeight: 600, color: "#111827" }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ActivityCard = ({ userId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("month");

  const getDateRange = () => {
    const now = new Date();
    let start = new Date();
    switch (range) {
      case "day":   start.setDate(now.getDate() - 1); break;
      case "week":  start.setDate(now.getDate() - 7); break;
      case "month": start.setMonth(now.getMonth() - 1); break;
      case "year":  start.setFullYear(now.getFullYear() - 1); break;
      default:      start.setDate(now.getDate() - 1);
    }
    return { start_date: toMysqlDatetime(start), end_date: toMysqlDatetime(now) };
  };

  useEffect(() => {
    const fetchClicks = async () => {
      setLoading(true);
      try {
        const { start_date, end_date } = getDateRange();
        const response = await api.get("/clicks/hours", {
          params: { user_id: userId, start_date, end_date },
        });
        const rawData = response.data.data;
        let chartData = [];

        if (range === "day") {
          const eventClicksByHour = {};
          Object.values(rawData).forEach(({ event_name, clicks_hours }) => {
            if (!eventClicksByHour[event_name]) eventClicksByHour[event_name] = {};
            for (const date in clicks_hours) {
              const hourlyClicks = clicks_hours[date];
              for (const hourStr in hourlyClicks) {
                const hour = parseInt(hourStr, 10);
                eventClicksByHour[event_name][hour] = (eventClicksByHour[event_name][hour] || 0) + hourlyClicks[hourStr];
              }
            }
          });
          const now = new Date();
          const currentHour = now.getHours();
          const hours = Array.from({ length: 24 }, (_, i) => (currentHour - i + 24) % 24).reverse();
          chartData = hours.map((hour) => {
            const row = { label: `${hour}h` };
            for (const eventName in eventClicksByHour) row[eventName] = eventClicksByHour[eventName][hour] || 0;
            return row;
          });
        } else if (range === "week" || range === "month") {
          const eventClicksByDay = {};
          Object.values(rawData).forEach(({ event_name, clicks_hours }) => {
            if (!eventClicksByDay[event_name]) eventClicksByDay[event_name] = {};
            for (const date in clicks_hours) {
              const totalDay = Object.values(clicks_hours[date]).reduce((a, b) => a + b, 0);
              eventClicksByDay[event_name][date] = (eventClicksByDay[event_name][date] || 0) + totalDay;
            }
          });
          const start = new Date(getDateRange().start_date);
          const end = new Date(getDateRange().end_date);
          const days = [];
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) days.push(d.toISOString().split("T")[0]);
          chartData = days.map((day) => {
            const row = { label: day };
            for (const eventName in eventClicksByDay) row[eventName] = eventClicksByDay[eventName][day] || 0;
            return row;
          });
        } else if (range === "year") {
          const eventClicksByMonth = {};
          Object.values(rawData).forEach(({ event_name, clicks_hours }) => {
            if (!eventClicksByMonth[event_name]) eventClicksByMonth[event_name] = {};
            for (const date in clicks_hours) {
              const totalDay = Object.values(clicks_hours[date]).reduce((a, b) => a + b, 0);
              const month = date.slice(0, 7);
              eventClicksByMonth[event_name][month] = (eventClicksByMonth[event_name][month] || 0) + totalDay;
            }
          });
          const start = new Date(getDateRange().start_date);
          const end = new Date(getDateRange().end_date);
          const months = [];
          for (let d = new Date(start.getFullYear(), start.getMonth(), 1); d <= end; d.setMonth(d.getMonth() + 1)) {
            months.push(`${d.getFullYear()}-${("0" + (d.getMonth() + 1)).slice(-2)}`);
          }
          chartData = months.map((month) => {
            const row = { label: month };
            for (const eventName in eventClicksByMonth) row[eventName] = eventClicksByMonth[eventName][month] || 0;
            return row;
          });
        }

        setData(chartData);
      } catch (err) {
        console.error("Failed to fetch activity data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchClicks();
  }, [userId, range]);

  return (
    <div style={{
      background: "#fff", borderRadius: "16px",
      border: "1px solid #f0f0f0",
      boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
      marginBottom: "24px", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 20px", borderBottom: "1px solid #f3f4f6",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: 34, height: 34, borderRadius: "10px",
            background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 17l5-5 4 4 9-9" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#111827" }}>Activity</span>
        </div>

        {/* Range pill selector */}
        <div style={{
          display: "inline-flex", background: "#f1f3f5",
          borderRadius: "8px", padding: "3px", gap: "2px",
        }}>
          {RANGE_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setRange(value)}
              style={{
                padding: "4px 12px", borderRadius: "6px",
                border: "none", fontSize: "12px", fontWeight: range === value ? 600 : 400,
                color: range === value ? "#111827" : "#6c757d",
                background: range === value ? "#fff" : "transparent",
                boxShadow: range === value ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                cursor: "pointer", transition: "all 0.15s", userSelect: "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div style={{ padding: "16px 8px 8px" }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, gap: "10px" }}>
            <Spinner animation="border" size="sm" style={{ color: "#6366f1" }} />
            <span style={{ fontSize: "13px", color: "#9ca3af" }}>Loading…</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data} margin={{ top: 4, right: 16, left: -8, bottom: 16 }}>
              <defs>
                {Object.keys(data[0] || {}).filter((k) => k !== "label").map((name, i) => (
                  <linearGradient key={name} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0.18} />
                    <stop offset="95%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                label={{
                  value: range === "day" ? "Hour" : range === "year" ? "Month" : "Day",
                  position: "insideBottom", offset: -8, fontSize: 11, fill: "#9ca3af",
                }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                label={{ value: "Visits", angle: -90, position: "insideLeft", fontSize: 11, fill: "#9ca3af" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }}
              />
              {Object.keys(data[0] || {})
                .filter((key) => key !== "label")
                .map((eventName, index) => (
                  <Area
                    key={eventName}
                    type="monotone"
                    dataKey={eventName}
                    stackId="1"
                    stroke={COLORS[index % COLORS.length]}
                    fill={`url(#grad-${index})`}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ActivityCard;
